<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
import { onMounted, ref, computed, watch } from 'vue'
import { SvgIcon } from "@progress/kendo-vue-common"
import { Popup } from '@progress/kendo-vue-popup'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { Input } from '@progress/kendo-vue-inputs'
import { Grid } from '@progress/kendo-vue-grid'
import { Pager } from '@progress/kendo-vue-data-tools'
import { filterIcon, arrowDownIcon, arrowUpIcon, searchIcon, filterClearIcon } from '@progress/kendo-svg-icons'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import { AOERMAMasterGetList, AOERMAMasterGetByNumber } from '@/services/RMAService'
import { GetRMAEntryUrl } from '@/stores/SettingsStore'
import { FormatCurrency } from '@/services/currencyFormatter.ts'

const { t } = useI18n()
const logger = useLogger()
setStoreLogger(logger)

// Some payee accounts have accumulated a large amount of historical RMAs, and fetching
// that entire history was causing RmaMasterGetList to time out. The "Created Date" filter
// below defaults to this recent window so the query stays bounded; users can widen it
// (or narrow it further) from the Filters panel.
const RMA_DEFAULT_LOOKBACK_DAYS = 30

function formatDateForInput(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getDefaultCreatedFromDateString(): string {
  const fromDate = new Date()
  fromDate.setDate(fromDate.getDate() - RMA_DEFAULT_LOOKBACK_DAYS)
  return formatDateForInput(fromDate)
}

function getDefaultCreatedToDateString(): string {
  return formatDateForInput(new Date())
}

// State variables
const rmas = ref<any[]>([])
const loading = ref<boolean>(false)
const noResults = ref<boolean>(false)
const searchText = ref<string>('')
const maxResults = ref<number>(20) // Default to 20 results
const currentPage = ref<number>(1)
const totalPages = ref<number>(1)
const totalItems = ref<number>(0)
const sortField = ref<string>('LastName')
const sortDir = ref<string>('asc')
const detailsPopupVisible = ref<boolean>(false)
const selectedRMA = ref<any | null>(null)
const filtersPanelVisible = ref<boolean>(false)
const filterNoTracking = ref<boolean>(false)
const filterDateFrom = ref<string>('')
const filterDateTo = ref<string>('')
// Defaults to the last RMA_DEFAULT_LOOKBACK_DAYS days so the initial RmaMasterGetList call
// stays bounded; editable in the Filters panel like any other filter.
const filterCreatedFrom = ref<string>(getDefaultCreatedFromDateString())
const filterCreatedTo = ref<string>(getDefaultCreatedToDateString())

// Initialize these as null first, we'll set them after defining the options
const selectedStatus = ref<any>({ text: t('rma.All'), value: 'All' })
const selectedColumnFilter = ref<any>(null)
const selectedOperatorFilter = ref<any>(null)
const selectedMaxResults = ref<any>({ value: 20, labelKey: 'labels.options.PageSize20', text: t('labels.options.PageSize20') }) // Separate object for dropdown binding

// Options for dropdowns - Updated according to requirements with actual status codes
const statusOptions = ref([
  { text: t('rma.All'), value: 'All' },
  { text: t('rma.Open'), value: 'OPEN' },
  { text: t('rma.WillBeShipped'), value: 'WILLBESHIPPED' },
  { text: t('rma.Closed'), value: 'CLOSED' },
  { text: t('rma.Cancelled'), value: 'CANCELLED' },
  { text: t('rma.OnHold'), value: 'ONHOLD' },
  { text: t('rma.ReturnExpected'), value: 'AWAITINGRETURN' },
  { text: t('rma.CreditDebitProcessingError'), value: 'PAYMENTPROCESSINGERROR' },
  { text: t('rma.ClosedWithoutReturns'), value: 'CLOSEDSHORT' },
  { text: t('rma.New'), value: 'NEW' },
  { text: t('rma.ManualReimbursementRequired'), value: 'MANUALREFUNDREQUIRED' },
  { text: t('rma.PhotoIsMissing'), value: 'MISSINGPHOTO' },
  { text: t('rma.ReceiptInProcess'), value: 'RECEIPTINPROCESS' },
  { text: t('rma.ReleaseAwaited'), value: 'INPROCESS' }
])

// We've already initialized the filter variables above

const maxResultsOptions = ref([
  { value: 5, labelKey: 'labels.options.PageSize5', text: t('labels.options.PageSize5') },
  { value: 10, labelKey: 'labels.options.PageSize10', text: t('labels.options.PageSize10') },
  { value: 20, labelKey: 'labels.options.PageSize20', text: t('labels.options.PageSize20') },
  { value: 25, labelKey: 'labels.options.PageSize25', text: t('labels.options.PageSize25') },
  { value: 50, labelKey: 'labels.options.PageSize50', text: t('labels.options.PageSize50') },
  { value: 100, labelKey: 'labels.options.PageSize100', text: t('labels.options.PageSize100') },
  { value: 175, labelKey: 'labels.options.PageSize175', text: t('labels.options.PageSize175') }
])

const columnFilterOptions = ref([
  { text: t('rma.LastName'), value: 'LastName' },
  { text: t('rma.FirstName'), value: 'FirstName' },
  { text: t('rma.DateOfSubmission'), value: 'SubmittedDate' }
])

const operatorFilterOptions = ref([
  { text: t('rma.StartsWith'), value: 'StartsWith' },
  { text: t('rma.EndsWith'), value: 'EndsWith' },
  { text: t('rma.Contains'), value: 'Contains' },
  { text: t('rma.Equals'), value: 'Equals' },
  { text: t('rma.Between'), value: 'Between' },
  { text: t('rma.GreaterThan'), value: 'GreaterThan' },
  { text: t('rma.LessThan'), value: 'LessThan' }
])

// Computed properties
const pagerSettings = computed(() => {
  return {
    type: 'numeric',
    buttonCount: 5,
    info: true,
    pageSizes: true,
    previousNext: true
  }
})

// State for storing filtered data
const filteredData = ref<any[]>([])

// Function to clear all filters
function clearFilters() {
  // Reset all filters to default values
  selectedStatus.value = statusOptions.value[0] // Reset to 'All'
  filterNoTracking.value = false
  filterDateFrom.value = ''
  filterDateTo.value = ''
  // Reset back to the last RMA_DEFAULT_LOOKBACK_DAYS days rather than clearing entirely,
  // so clearing filters can't reintroduce an unbounded (timeout-prone) query.
  filterCreatedFrom.value = getDefaultCreatedFromDateString()
  filterCreatedTo.value = getDefaultCreatedToDateString()
  searchText.value = ''
  selectedMaxResults.value = { value: 20, labelKey: 'labels.options.PageSize20', text: t('labels.options.PageSize20') } // Reset to default 20 results
  maxResults.value = 20

  // Re-fetch with status reset to 'All'. This still defaults to the last
  // RMA_DEFAULT_LOOKBACK_DAYS days (see searchRMAs) rather than pulling full history.
  searchRMAs()
}

function toggleFiltersPanel() {
  filtersPanelVisible.value = !filtersPanelVisible.value
}

function closeFiltersPanel() {
  filtersPanelVisible.value = false
}

// Function to apply filters
function applyFilters() {
  // Ensure rmas.value is an array
  if (!Array.isArray(rmas.value)) {
    filteredData.value = []
    return
  }

  // Start with all RMAs
  let filtered = [...rmas.value]

  // 1. Apply Complaint Status filter
  if (selectedStatus.value && selectedStatus.value.value !== 'All') {
    filtered = filtered.filter(rma => {
      // Make sure we're comparing case-insensitively and handle null/undefined values
      if (!rma.CodeIdRmaStatus) return false;
      return String(rma.CodeIdRmaStatus).toUpperCase() === String(selectedStatus.value.value).toUpperCase();
    })
  }

  // 2. Apply No Tracking filter
  if (filterNoTracking.value) {
    filtered = filtered.filter(rma => !rma.TrackingNumber || rma.TrackingNumber.trim() === '')
  }

  // 3. Apply Date Range filter
  if (filterDateFrom.value || filterDateTo.value) {
    filtered = filtered.filter(rma => {
      if (!rma.SubmittedDate) return false
      const submittedDate = new Date(rma.SubmittedDate)

      if (filterDateFrom.value) {
        const fromDate = new Date(filterDateFrom.value)
        if (submittedDate < fromDate) return false
      }

      if (filterDateTo.value) {
        const toDate = new Date(filterDateTo.value)
        if (submittedDate > toDate) return false
      }

      return true
    })
  }

  // 4. Apply search text filter (search by name or reference number)
  if (searchText.value.trim()) {
    const searchValue = searchText.value.trim().toLowerCase()
    // Search by name (FirstName, LastName) or RMA Number
    filtered = filtered.filter(rma => {
      const firstName = (rma.FirstName || '').toLowerCase()
      const lastName = (rma.LastName || '').toLowerCase()
      const rmaNumber = (rma.RMANumber || '').toLowerCase()

      return firstName.includes(searchValue) ||
             lastName.includes(searchValue) ||
             rmaNumber.includes(searchValue)
    })
  }

  // Apply sorting to filtered data
  filtered.sort((a, b) => {
    let aValue = a[sortField.value]
    let bValue = b[sortField.value]

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1

    // Handle date sorting
    if (sortField.value === 'SubmittedDate') {
      aValue = aValue instanceof Date ? aValue.getTime() : new Date(aValue).getTime()
      bValue = bValue instanceof Date ? bValue.getTime() : new Date(bValue).getTime()
    }

    // Handle numeric sorting
    if (sortField.value === 'FeeTotal' || sortField.value === 'Total') {
      aValue = Number(aValue) || 0
      bValue = Number(bValue) || 0
    }

    // Handle string sorting (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }

    // Compare values
    if (aValue < bValue) return sortDir.value === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })

  // Update filtered data
  filteredData.value = filtered

  // Update total items and pages based on filtered results
  totalItems.value = filtered.length
  // Make sure we extract the numeric value from the dropdown selection
  maxResults.value = selectedMaxResults.value?.value || 20
  totalPages.value = Math.ceil(filtered.length / maxResults.value) || 1

  // Update noResults flag
  noResults.value = filtered.length === 0

  // Reset to first page when applying filters
  currentPage.value = 1

  console.log('Filters applied:', {
    status: selectedStatus.value,
    searchText: searchText.value,
    column: selectedColumnFilter.value,
    operator: selectedOperatorFilter.value,
    resultsCount: filtered.length
  })
}

// Computed property to get the current page of data
const filteredRMAs = computed(() => {
  return filteredData.value
})

const displayedRMAs = computed(() => {
  // Get paginated slice of filtered RMAs
  const startIndex = (currentPage.value - 1) * maxResults.value
  const endIndex = Math.min(startIndex + maxResults.value, filteredRMAs.value.length)
  return filteredRMAs.value.slice(startIndex, endIndex)
})

// No automatic filtering when changing filters - only when search button is clicked

// Methods
async function searchRMAs() {
  loading.value = true
  SetIsLoading(true)

  try {
    // Scope the backend query by the Created Date range and selected status (if not 'All').
    // This keeps the RmaMasterGetList call from having to return a user's entire RMA history.
    const createdTimeFromUtc = filterCreatedFrom.value
      ? new Date(`${filterCreatedFrom.value}T00:00:00`).toISOString()
      : undefined
    const createdTimeToUtc = filterCreatedTo.value
      ? new Date(`${filterCreatedTo.value}T23:59:59.999`).toISOString()
      : undefined

    const data = await AOERMAMasterGetList({
      rmaStatusId: selectedStatus.value && selectedStatus.value.value !== 'All' ? selectedStatus.value.value : undefined,
      createdTimeFromUtc,
      createdTimeToUtc
    })
    console.log('API response:', data)

    // Ensure data is an array before updating component state
    const rmaArray = Array.isArray(data) ? data : []
    console.log('RMA array:', rmaArray)

    // Map the API data to the structure needed by the table
    const mappedRMAs = rmaArray.map(item => {
      // Create an object that complies with the AOERMAMasterViewModel interface
      return {
        // Fields needed for the table
        RMANumber: item.rmaNumber,
        CodeIdRmaStatus: item.codeIdRmaStatus,
        CodeTextRmaStatus: item.codeTextRmaStatus,
        SubmittedDate: item.submittedDate ? new Date(item.submittedDate) : undefined,
        FeeTotal: item.feeTotal,
        Total: item.total,
        FirstName: item.firstName,
        LastName: item.lastName,
        TrackingNumber: item.trackingNumber || '',

        // Other fields required by the interface
        RmaNote: item.rmaNote || '',
        ShippingAmount: item.shippingAmount || 0,
        ShippingMethodName: item.shippingMethodName || '',
        Links: item.links || ''
      }
    })

    // Update component state with the results
    rmas.value = mappedRMAs

    /* TEAU-1239: Mock data commented out - was for testing only
    const mockData = [
      { RMANumber: 'RMA-DE-001', CodeIdRmaStatus: 'OPEN', CodeTextRmaStatus: 'Open', SubmittedDate: new Date('2026-03-15'), FeeTotal: 12.50, Total: 89.90, FirstName: 'Klaus', LastName: 'Müller', TrackingNumber: '' },
      { RMANumber: 'RMA-FR-002', CodeIdRmaStatus: 'CLOSED', CodeTextRmaStatus: 'Closed', SubmittedDate: new Date('2026-02-10'), FeeTotal: 5.00, Total: 134.75, FirstName: 'Marie', LastName: 'Dupont', TrackingNumber: '1Z999AA10123456784' },
    ]
    rmas.value.push(...mockData)
    */

    // Apply filters to the loaded data
    applyFilters()

    console.log('Data loaded and filtered:', {
      totalItems: totalItems.value,
      totalPages: totalPages.value,
      noResults: noResults.value,
      displayedCount: Math.min(maxResults.value, filteredData.value.length)
    })
  } catch (error) {
    logger.error('Error fetching RMAs:', error)
    noResults.value = true
    rmas.value = []
    filteredData.value = []
    totalItems.value = 0
    totalPages.value = 1
  } finally {
    loading.value = false
    SetIsLoading(false)
  }
}

function handlePageChange(event: any) {
  currentPage.value = event.page
}

function handleSortChange(field: string) {
  if (sortField.value === field) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDir.value = 'asc'
  }
  // Re-apply filters which will also apply the new sort
  applyFilters()
}

async function viewRMADetails(rma: any) {
  SetIsLoading(true)
  try {
    // Get detailed RMA information
    const detailedRMA = await AOERMAMasterGetByNumber(rma.RMANumber)

    // If we got detailed information, use it; otherwise use the row data
    selectedRMA.value = detailedRMA || rma
    detailsPopupVisible.value = true
  } catch (error) {
    logger.error('Error fetching RMA details:', error)
    // Fall back to the row data if the API call fails
    selectedRMA.value = rma
    detailsPopupVisible.value = true
  } finally {
    SetIsLoading(false)
  }
}

function closeDetailsPopup() {
  detailsPopupVisible.value = false
  selectedRMA.value = null
}

async function redirectToNewRMA() {
  logger.debug('Redirecting to new RMA page')
  const rmaEntryUrl = await GetRMAEntryUrl()
  window.location.href = rmaEntryUrl
}

// Método para obtener la clase de Bootstrap para los badges de estado
function getBadgeClass(status: string | undefined): string {
  if (!status) return 'bg-secondary'

  const statusLower = status.toLowerCase()
  switch (statusLower) {
    case 'open':
      return 'bg-success'
    case 'closed':
      return 'bg-secondary'
    case 'cancelled':
      return 'bg-danger'
    case 'onhold':
      return 'bg-warning'
    case 'new':
      return 'bg-primary'
    case 'awaitingreturn':
      return 'bg-purple'
    case 'willbeshipped':
      return 'bg-info'
    case 'missingphoto':
      return 'bg-orange'
    case 'paymentprocessingerror':
      return 'bg-pink'
    case 'closedshort':
      return 'bg-dark'
    case 'manualrefundrequired':
      return 'bg-brown'
    case 'receiptinprocess':
      return 'bg-teal'
    case 'inprocess':
      return 'bg-indigo'
    default:
      return 'bg-secondary'
  }
}

// Initialize component
onMounted(() => {
  logger.debug('AOERMAs component mounted')

  searchRMAs()
})
</script>

<template>
  <div class="rma-container">
    <div class="page-header">
      <div class="header-content">
        <h1 class="main-title">{{ t('rma.ProductReturnsTitle') }}</h1>
        <div class="header-row">
          <div class="header-left">
            <h2 class="subtitle">{{ t('rma.AllProductReturns') }}</h2>
            <p class="description">{{ t('rma.KeepTrackDescription') }}</p>
          </div>
          <div class="header-right">
            <button class="new-return-btn" @click="redirectToNewRMA">
              {{ t('rma.NewProductReturn') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="search-filters-row">
      <div class="search-wrapper">
        <Input
          v-model="searchText"
          class="search-input-field"
          :placeholder="t('rma.SearchByNameOrReference')"
          @keyup.enter="applyFilters"
        />
      </div>
      <button class="filters-btn" @click="toggleFiltersPanel">
        <SvgIcon :icon="filterIcon" :size="'small'"></SvgIcon>
        {{ t('rma.Filters') }}
      </button>
    </div>

    <!-- Filters Side Panel -->
    <transition name="slide-fade">
      <div v-if="filtersPanelVisible" class="filters-panel-overlay" @click="closeFiltersPanel"></div>
    </transition>
    <transition name="slide">
      <div v-if="filtersPanelVisible" class="filters-panel">
        <div class="filters-panel-header">
          <h3>{{ t('rma.Filters') }}</h3>
          <button class="close-btn" @click="closeFiltersPanel">
            <span>&times;</span>
          </button>
        </div>
        <div class="filters-panel-content">
          <div class="filter-group">
            <label class="filter-label">{{ t('rma.CreatedDate') }}</label>
            <div class="date-range-inputs">
              <input
                type="date"
                v-model="filterCreatedFrom"
                class="date-input"
                :placeholder="t('rma.From')"
              />
              <span class="date-separator">-</span>
              <input
                type="date"
                v-model="filterCreatedTo"
                class="date-input"
                :placeholder="t('rma.To')"
              />
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">{{ t('rma.DateOfSubmission') }}</label>
            <div class="date-range-inputs">
              <input
                type="date"
                v-model="filterDateFrom"
                class="date-input"
                :placeholder="t('rma.From')"
              />
              <span class="date-separator">-</span>
              <input
                type="date"
                v-model="filterDateTo"
                class="date-input"
                :placeholder="t('rma.To')"
              />
            </div>
          </div>

          <div class="filter-group">
            <label class="filter-label">{{ t('rma.Status') }}</label>
            <DropDownList
              :data-items="statusOptions"
              :text-field="'text'"
              :value-field="'value'"
              v-model="selectedStatus"
              class="filter-dropdown"
            />
          </div>

          <div class="filter-group">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input
                type="checkbox"
                class="k-checkbox"
                v-model="filterNoTracking"
                style="width: 16px; height: 16px; cursor: pointer;"
              />
              <span class="text-small" style="color: #555;">{{ t('rma.ReturnsWithNoTracking') }}</span>
            </label>
          </div>

          <div class="filters-panel-actions">
            <button class="btn-apply-small" @click="searchRMAs(); closeFiltersPanel()">
              {{ t('rma.ApplyFilters') }}
            </button>
            <button class="btn-clear-small" @click="clearFilters()">
              {{ t('rma.ClearFilters') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{$t('labels.Loading')}}</span>
      </div>
    </div>

    <div v-else-if="noResults" class="text-center my-5">
      <p class="no-results">{{ t('rma.NoComplaintsFound') }}</p>
    </div>

    <div v-else>
      <!-- Desktop Table View -->
      <div class="rma-desktop-grid-view">
      <div class="card mb-3" style="border: 1px solid black; box-shadow: 0 1px 3px black; border-radius: 8px; overflow: hidden;">
        <div class="table-responsive">
          <table class="table table-hover mb-0" style="margin-bottom: 0 !important; border: none !important; border-collapse: separate !important;">
            <thead class="table-light">
              <tr>
                <th @click="handleSortChange('LastName')" class="cursor-pointer">
                  {{ t('rma.LastName') }}
                  <SvgIcon v-if="sortField === 'LastName'" :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" :size="'small'"></SvgIcon>
                  <SvgIcon v-else :icon="filterIcon" :size="'small'"></SvgIcon>
                </th>
                <th @click="handleSortChange('FirstName')" class="cursor-pointer">
                  {{ t('rma.FirstName') }}
                  <SvgIcon v-if="sortField === 'FirstName'" :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" :size="'small'"></SvgIcon>
                  <SvgIcon v-else :icon="filterIcon" :size="'small'"></SvgIcon>
                </th>
                <th @click="handleSortChange('RMANumber')" class="cursor-pointer" style="white-space: nowrap;">
                  {{ t('rma.RMANumber') }}
                  <span v-if="sortField === 'RMANumber'" class="sort-icon" style="opacity: 0.7;">
                    <SvgIcon :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" />
                  </span>
                </th>
                <th @click="handleSortChange('SubmittedDate')" class="cursor-pointer">
                  {{ t('rma.DateOfSubmission') }}
                  <SvgIcon v-if="sortField === 'SubmittedDate'" :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" :size="'small'"></SvgIcon>
                  <SvgIcon v-else :icon="filterIcon" :size="'small'"></SvgIcon>
                </th>
                <th @click="handleSortChange('CodeTextRmaStatus')" class="cursor-pointer">
                  {{ t('rma.Status') }}
                  <SvgIcon v-if="sortField === 'CodeTextRmaStatus'" :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" :size="'small'"></SvgIcon>
                  <SvgIcon v-else :icon="filterIcon" :size="'small'"></SvgIcon>
                </th>
                <th @click="handleSortChange('FeeTotal')" class="cursor-pointer">
                  {{ t('rma.FeeTotal') }}
                  <SvgIcon v-if="sortField === 'FeeTotal'" :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" :size="'small'"></SvgIcon>
                  <SvgIcon v-else :icon="filterIcon" :size="'small'"></SvgIcon>
                </th>
                <th @click="handleSortChange('Total')" class="cursor-pointer">
                  {{ t('rma.Total') }}
                  <SvgIcon v-if="sortField === 'Total'" :icon="sortDir === 'asc' ? arrowUpIcon : arrowDownIcon" :size="'small'"></SvgIcon>
                  <SvgIcon v-else :icon="filterIcon" :size="'small'"></SvgIcon>
                </th>
                <th>{{ t('rma.TrackingNumber') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rma in displayedRMAs" :key="rma.RMANumber">
                <td>{{ rma.LastName }}</td>
                <td>{{ rma.FirstName }}</td>
                <td>
                  <a href="#" class="text-primary" @click.prevent="viewRMADetails(rma)">{{ rma.RMANumber }}</a>
                </td>
                <td>{{ rma.SubmittedDate ? new Date(rma.SubmittedDate).toLocaleDateString() : '' }}</td>
                <td>
                  <span class="badge" :class="getBadgeClass(rma.CodeIdRmaStatus)">
                    {{ rma.CodeTextRmaStatus }}
                  </span>
                </td>
                <td>{{ FormatCurrency(rma.FeeTotal, null) }}</td>
                <td>{{ FormatCurrency(rma.Total, null) }}</td>
                <td>{{ rma.TrackingNumber || t('rma.NoTrackingProvided') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </div>

      <!-- Mobile Card View -->
      <div class="rma-mobile-card-view">
        <div v-for="rma in displayedRMAs" :key="rma.RMANumber" class="rma-card">
          <div class="rma-card-header">
            <a href="#" class="rma-card-title" @click.prevent="viewRMADetails(rma)">
              {{ t('rma.RMANumber') }} {{ rma.RMANumber }}
            </a>
            <span class="badge" :class="getBadgeClass(rma.CodeIdRmaStatus)">
              {{ rma.CodeTextRmaStatus }}
            </span>
          </div>
          <div class="rma-card-body">
            <div class="rma-card-row">
              <span class="rma-card-label">{{ t('rma.LastName') }}</span>
              <span class="rma-card-value">{{ rma.LastName }}</span>
            </div>
            <div class="rma-card-row">
              <span class="rma-card-label">{{ t('rma.FirstName') }}</span>
              <span class="rma-card-value">{{ rma.FirstName }}</span>
            </div>
            <div class="rma-card-row">
              <span class="rma-card-label">{{ t('rma.DateOfSubmission') }}</span>
              <span class="rma-card-value">{{ rma.SubmittedDate ? new Date(rma.SubmittedDate).toLocaleDateString() : '' }}</span>
            </div>
            <div class="rma-card-row">
              <span class="rma-card-label">{{ t('rma.FeeTotal') }}</span>
              <span class="rma-card-value">${{ rma.FeeTotal?.toFixed(2) }}</span>
            </div>
            <div class="rma-card-row">
              <span class="rma-card-label">{{ t('rma.Total') }}</span>
              <span class="rma-card-value">${{ rma.Total?.toFixed(2) }}</span>
            </div>
            <div class="rma-card-row">
              <span class="rma-card-label">{{ t('rma.TrackingNumber') }}</span>
              <span class="rma-card-value">{{ rma.TrackingNumber || t('rma.NoTrackingProvided') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-3" style="border: 1px solid black; box-shadow: 0 1px 3px black; border-radius: 8px;">
        <div class="card-body d-flex justify-content-between align-items-center">
          <div class="pagination-info text-small" style="color: #757575;">
            {{ t('rma.Showing') }} {{ filteredRMAs.length > 0 ? (currentPage - 1) * maxResults + 1 : 0 }} {{ t('rma.To') }} {{ Math.min(currentPage * maxResults, filteredRMAs.length) }} {{ t('rma.Of') }} {{ filteredRMAs.length }} {{ t('rma.Entries') }}
          </div>
          <div class="btn-group">
            <button
              class="btn btn-outline-secondary pagination-btn text-small"
              style="border-color: black; height: 30px; padding: 0 0.8rem;"
              :disabled="currentPage === 1"
              @click="currentPage--"
            >
              {{ t('rma.Previous') }}
            </button>
            <button
              class="btn btn-outline-secondary active pagination-btn text-small font-medium"
              style="border-color: black; height: 30px; padding: 0 0.8rem; background-color: black;"
              disabled
            >
              {{ currentPage }}
            </button>
            <button
              class="btn btn-outline-secondary pagination-btn text-small"
              style="border-color: black; height: 30px; padding: 0 0.8rem;"
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              {{ t('rma.Next') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- RMA Details Popup -->
    <Popup
      v-if="selectedRMA"
      :show="detailsPopupVisible"
      :popup-class="'rma-details-popup'"
    >
      <div class="rma-details-container">
        <div class="rma-details-header" style="background-color: white; color: #333; border-bottom: 1px solid #e9ecef; padding: 20px 25px; border-radius: 8px 8px 0 0;">
          <div class="rma-header-content">
            <h2 class="text-h3" style="margin: 0; color: #333;">{{ t('rma.RMADetails') }}</h2>
            <div class="rma-number-status" style="display: flex; align-items: center; margin-top: 5px;">
              <span class="rma-number">{{ t('rma.RMANumber') }}: <strong>{{ selectedRMA.item?.rmaNumber || selectedRMA.RMANumber }}</strong></span>
              <span class="badge text-caption" :class="getBadgeClass(selectedRMA.item?.codeIdRmaStatus || selectedRMA.CodeIdRmaStatus)" style="margin-left: 10px; display: inline-flex; align-items: center; justify-content: center; padding: 0.4em 0.7em;">
                {{ selectedRMA.item?.codeTextRmaStatus || selectedRMA.CodeTextRmaStatus }}
              </span>
            </div>
          </div>
          <button class="btn-close text-h4" @click="closeDetailsPopup" :aria-label="t('buttons.Close')" style="background: transparent; border: none; color: #666; cursor: pointer;">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="rma-details-content" style="padding: 24px;">
          <div class="rma-details-grid" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 24px;">
            <!-- Customer Information Section -->
            <div class="rma-details-section" style="display: flex; flex-direction: column; margin-bottom: 24px;">
              <h3 class="section-title text-body font-semibold" style="margin-bottom: 14px; color: #444; border-bottom: 1px solid black; padding-bottom: 8px;">
                {{ t('rma.CustomerInformation') }}
              </h3>
              <div class="rma-details-card" style="background-color: var(--vt-c-white); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(black, 0.1); border: 1px solid black; height: 100%;">
                <div class="rma-details-row" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.Name') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ selectedRMA.item?.customer?.firstName || selectedRMA.FirstName }} {{ selectedRMA.item?.customer?.lastName || selectedRMA.LastName }}</div>
                </div>
                <div class="rma-details-row" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.AOEOrderId') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ selectedRMA.item?.aoeOrderId || selectedRMA.AOEOrderId || t('rma.NotAvailable') }}</div>
                </div>
                <div class="rma-details-row" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.DateOfSubmission') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ (selectedRMA.item?.submittedDate || selectedRMA?.SubmittedDate) ? new Date(selectedRMA.item?.submittedDate || selectedRMA.SubmittedDate).toLocaleDateString() : t('rma.NotAvailable') }}</div>
                </div>
              </div>
            </div>

            <!-- Complaint Details Section -->
            <div class="rma-details-section" style="display: flex; flex-direction: column; margin-bottom: 24px;">
              <h3 class="section-title text-body font-semibold" style="margin-bottom: 14px; color: #444; border-bottom: 1px solid black; padding-bottom: 8px;">
                {{ t('rma.ComplaintDetails') }}
              </h3>
              <div class="rma-details-card" style="background-color: var(--vt-c-white); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(black, 0.1); border: 1px solid black; height: 100%;">
                <div class="rma-details-row" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.Status') }}</div>
                  <div class="rma-details-value status-value text-body" :class="'text-' + (selectedRMA.item?.codeIdRmaStatus || selectedRMA.CodeIdRmaStatus)?.toLowerCase()" style="width: 60%; color: #333;">
                    {{ selectedRMA.item?.codeTextRmaStatus || selectedRMA.CodeTextRmaStatus }}
                  </div>
                </div>
                <div class="rma-details-row" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.TrackingNumber') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ selectedRMA.item?.trackingNumber || selectedRMA.TrackingNumber || t('rma.NoTrackingProvided') }}</div>
                </div>
                <div class="rma-details-row" v-if="selectedRMA.item?.closedDate || selectedRMA.ClosedDate" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.ClosedDate') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ new Date(selectedRMA.item?.closedDate || selectedRMA.ClosedDate).toLocaleDateString() }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Product Information Section -->
          <div class="rma-details-section full-width" style="grid-column: 1 / -1; display: flex; flex-direction: column; margin-bottom: 24px;">
            <h3 class="section-title text-body font-semibold" style="margin-bottom: 14px; color: #444; border-bottom: 1px solid black; padding-bottom: 8px;">
              {{ t('rma.ProductInformation') }}
            </h3>
            <div class="rma-details-card" style="background-color: var(--vt-c-white); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(black, 0.1); border: 1px solid black;">
              <!-- Inbound SKUs (Items Being Returned) -->
              <div v-if="selectedRMA.item?.inboundSkus && selectedRMA.item.inboundSkus.length > 0" style="margin-bottom: 20px;">
                <h4 class="text-body font-semibold" style="color: #d32f2f; margin-bottom: 12px;">{{ t('rma.ItemsBeingReturned') }}</h4>
                <div v-for="(sku, index) in selectedRMA.item.inboundSkus" :key="'inbound-' + index" style="padding: 12px; margin-bottom: 8px; background-color: #ffebee; border-radius: 2px; border-left: 3px solid #d32f2f;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="flex: 1;">
                      <div class="product-name">{{ sku.productName }}</div>
                      <div class="text-small" style="color: #666;">{{ t('labels.SkuPrefix', { sku: sku.skuCode }) }}</div>
                      <div class="text-small" style="color: #666;" v-if="sku.reasonName">{{ t('rma.Reason') }}: {{ sku.reasonName }}</div>
                    </div>
                    <div style="text-align: right;">
                      <div class="font-semibold" style="color: #333;">{{ t('labels.Quantity') }}: {{ sku.quantity }}</div>
                      <div class="text-body" style="color: #666;">{{ FormatCurrency(sku.price, null) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Outbound SKUs (Replacement Items) -->
              <div v-if="selectedRMA.item?.outboundSkus && selectedRMA.item.outboundSkus.length > 0" style="margin-bottom: 20px;">
                <h4 class="text-body font-semibold" style="color: #2e7d32; margin-bottom: 12px;">{{ t('rma.ReplacementItems') }}</h4>
                <div v-for="(sku, index) in selectedRMA.item.outboundSkus" :key="'outbound-' + index" style="padding: 12px; margin-bottom: 8px; background-color: #e8f5e9; border-radius: 2px; border-left: 3px solid #2e7d32;">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <div style="flex: 1;">
                      <div class="product-name">{{ sku.productName }}</div>
                      <div class="text-small" style="color: #666;">{{ t('labels.SkuPrefix', { sku: sku.skuCode }) }}</div>
                    </div>
                    <div style="text-align: right;">
                      <div class="font-semibold" style="color: #333;">{{ t('labels.Quantity') }}: {{ sku.quantity }}</div>
                      <div class="text-body" style="color: #666;">{{ FormatCurrency(sku.price, null) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="rma-details-row" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.ShippingMethod') }}</div>
                <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ selectedRMA.item?.order?.shippingMethodName || selectedRMA.ShippingMethodName || t('rma.NotAvailable') }}</div>
              </div>
            </div>
          </div>

          <!-- Financial Information Section -->
          <div class="rma-details-section full-width" style="grid-column: 1 / -1; display: flex; flex-direction: column; margin-bottom: 24px;">
            <h3 class="section-title text-body font-semibold" style="margin-bottom: 14px; color: #444; border-bottom: 1px solid black; padding-bottom: 8px;">
              {{ t('rma.RefundInformation') }}
            </h3>
            <div class="rma-details-card" style="background-color: var(--vt-c-white); border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(black, 0.1); border: 1px solid black;">
              <div class="financial-summary" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px;">
                <div class="financial-item" style="text-align: center; padding: 14px; background-color: var(--vt-c-white); border-radius: 6px; border: 1px solid black; box-shadow: 0 1px 2px black;">
                  <div class="financial-label text-small font-medium" style="color: #666; margin-bottom: 8px;">{{ t('rma.FeeTotal') }}</div>
                  <div class="financial-value text-body-large font-semibold" style="color: #333;">{{ FormatCurrency(selectedRMA.item?.feeTotal || selectedRMA.FeeTotal, null) || '0.00' }}</div>
                </div>
                <div class="financial-item" style="text-align: center; padding: 14px; background-color: var(--vt-c-white); border-radius: 6px; border: 1px solid black; box-shadow: 0 1px 2px black;">
                  <div class="financial-label text-small font-medium" style="color: #666; margin-bottom: 8px;">{{ t('rma.ShippingAmount') }}</div>
                  <div class="financial-value text-body-large font-semibold" style="color: #333;">{{ FormatCurrency(selectedRMA.item?.shippingAmount || selectedRMA.ShippingAmount, null) || '0.00' }}</div>
                </div>
                <div class="financial-item financial-total" style="text-align: center; padding: 14px; background-color: var(--vt-c-white); border-radius: 6px; border: 1px solid black; box-shadow: 0 1px 2px black;">
                  <div class="financial-label text-small font-medium" style="color: #666; margin-bottom: 8px;">{{ t('rma.RefundAmount') }}</div>
                  <div class="financial-value text-body-large font-semibold" style="color: #2e7d32;">{{ FormatCurrency(selectedRMA.item?.total || selectedRMA.Total, null) || '0.00' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Return Status Section -->
          <div class="rma-details-section full-width" style="grid-column: 1 / -1; display: flex; flex-direction: column; margin-bottom: 24px;">
            <h3 class="section-title text-body font-semibold" style="margin-bottom: 14px; color: #444; border-bottom: 1px solid black; padding-bottom: 8px;">
              {{ t('rma.ReturnStatus') }}
            </h3>
            <div class="rma-details-card" style="background-color: var(--vt-c-white); border-radius: 6px; padding: 16px; box-shadow: 0 1px 3px rgba(black, 0.1); border: 1px solid black;">
              <div class="return-status-badge text-body font-medium" :class="'status-' + (selectedRMA.item?.codeIdRmaStatus || selectedRMA.CodeIdRmaStatus)?.toLowerCase()" style="display: inline-block; padding: 8px 16px; border-radius: 4px; text-align: center; margin-bottom: 16px;">
                {{ selectedRMA.item?.codeTextRmaStatus || selectedRMA.CodeTextRmaStatus }}
              </div>
              <div class="return-details mt-3">
                <div class="rma-details-row" v-if="selectedRMA.item?.isSubmitted || selectedRMA.IsSubmitted" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.IsSubmitted') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">
                    <span class="status-indicator status-success text-small" style="display: inline-block; padding: 2px 8px; border-radius: 3px; background-color: #e8f5e9; color: #2e7d32;">{{ t('rma.Yes') }}</span>
                  </div>
                </div>
                <div class="rma-details-row" v-if="selectedRMA.item?.exportDate || selectedRMA.ExportDate" style="display: flex; margin-bottom: 12px; align-items: baseline; flex-wrap: wrap;">
                  <div class="rma-details-label text-small font-medium" style="width: 40%; color: #666; padding-right: 10px;">{{ t('rma.ExportDate') }}</div>
                  <div class="rma-details-value text-body" style="width: 60%; color: #333;">{{ new Date(selectedRMA.item?.exportDate || selectedRMA.ExportDate).toLocaleDateString() }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Section -->
          <div class="rma-details-section full-width" v-if="selectedRMA.RmaNote" style="grid-column: 1 / -1; display: flex; flex-direction: column; margin-bottom: 24px;">
            <h3 class="section-title text-body font-semibold" style="margin-bottom: 14px; color: #444; border-bottom: 1px solid black; padding-bottom: 8px;">
              {{ t('rma.Notes') }}
            </h3>
            <div class="rma-details-card rma-note" style="background-color: var(--vt-c-white); border-radius: 6px; padding: 16px; box-shadow: 0 1px 3px rgba(black, 0.1); border: 1px solid black;">
              <p class="text-body" style="margin: 0; color: #333;">{{ selectedRMA.RmaNote }}</p>
            </div>
          </div>
        </div>

        <div class="rma-details-footer" style="padding: 16px 24px; border-top: 1px solid #e9ecef; display: flex; justify-content: flex-end; border-radius: 0 0 8px 8px;">
          <button class="btn btn-outline-secondary text-body" @click="closeDetailsPopup" style="background-color: transparent; border: 1px solid ccc; color: #666; padding: 6px 16px; border-radius: 4px; cursor: pointer; transition: all 0.2s ease;">
            {{ t('rma.Close') }}
          </button>
        </div>
      </div>
    </Popup>
  </div>
</template>

<style lang="scss">
/* Kendo Input Overrides */
.rma-container .k-input-inner {
  @include typography-small;
  height: 38px !important;
  padding: $spacing-2 $spacing-4 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: $radius-md !important;
  box-shadow: none !important;
  background-color: white !important;
  line-height: 1.5 !important;
}

.rma-container .k-input-inner:focus {
  /* !important required: Override Kendo input focus border which uses --kendo-color-primary */
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark)) !important;
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1) !important;
}
.table,
.table th,
.table td,
.table thead,
.table tbody,
.table tr {
  border-color: black !important;
  border-width: 0 !important;
}

.table th {
  border-bottom-width: 1px !important;
  border-bottom-color: rgba(black, 0.1) !important;
}

.table td {
  border-bottom-width: 1px !important;
}

.table tbody tr:last-child td {
  border-bottom-width: 0 !important;
}

.table {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

/* Estilos personalizados para badges - colores más suaves y elegantes */
.bg-purple {
  background-color: rgba(#9c27b0, 0.85) !important;
  color: white !important;
}

.bg-orange {
  background-color: rgba(#ff5722, 0.85) !important;
  color: white !important;
}

.bg-pink {
  background-color: rgba(#e91e63, 0.85) !important;
  color: white !important;
}

.bg-teal {
  background-color: rgba(#009688, 0.85) !important;
  color: white !important;
}

.bg-indigo {
  background-color: rgba(#673ab7, 0.85) !important;
  color: white !important;
}

.bg-brown {
  background-color: rgba(#795548, 0.85) !important;
  color: white !important;
}

/* Colores Bootstrap más suaves */
.bg-primary {
  background-color: rgba(#0d6efd, 0.85) !important;
}

.bg-success {
  background-color: rgba(#198754, 0.85) !important;
}

.bg-danger {
  background-color: rgba(#dc2626, 0.85) !important;
}

.bg-warning {
  background-color: rgba(#ffc107, 0.85) !important;
  color: #212529 !important;
}

.bg-info {
  background-color: rgba(#0dcaf0, 0.85) !important;
}

.bg-dark {
  background-color: rgba(#212529, 0.85) !important;
}

.bg-secondary {
  background-color: rgba(#6c757d, 0.85) !important;
}

.cursor-pointer {
  cursor: pointer;
}


.rma-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(black, 0.1);
}

.rma-table th, .rma-table td {
  padding: $spacing-3 $spacing-4;
  text-align: left;
  border-bottom: 1px solid black;
}

.rma-table th {
  @include font-weight-medium;
  background-color: rgba(#fefefe, 0.7);
  color: #5c6b7a;
  letter-spacing: 0.3px;
  border-bottom: 1px solid rgba(black, 0.1);
}

.rma-table th:hover {
  background-color: rgba(#e9ecef, 0.7);
}

.rma-table tr:hover {
  background-color: var(--vt-c-white-soft);
}

.rma-table tr:last-child td {
  border-bottom: none;
}

/* Status Badge Styles */
.status-badge, .return-status-badge, .rma-status-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  padding: $spacing-1 $spacing-2-5;
  border-radius: $radius-2xl;
  color: white;
  text-align: center;
  min-width: 100px;
}

.return-status-badge {
  @include typography-body;
  padding: $spacing-2 $spacing-4;
  margin-bottom: $spacing-4;
  min-width: 120px;
}

.rma-status-badge {
  background-color: rgba(white, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Estilos para la tabla */
.table {
  border-collapse: separate;
  border-spacing: 0;
  box-shadow: 0 1px 6px black;
}

.table th, .table td {
  border-top: none;
  border-bottom: 1px solid black;
  padding: $spacing-3 $spacing-4;
}

.table th {
  @include font-weight-medium;
  color: #5c6b7a;
  letter-spacing: 0.3px;
  border-bottom: 1px solid rgba(black, 0.1);
  background-color: rgba(#fefefe, 0.7);
}

.table-hover tbody tr:hover {
  background-color: var(--vt-c-white-soft);
}

.table-light {
  background-color: var(--vt-c-white-soft);
}

/* Eliminar bordes en la última fila */
.table tbody tr:last-child td {
  border-bottom: none;
}

.rma-link {
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  text-decoration: underline;
  cursor: pointer;
}

.no-results {
  @include typography-body;
  color: #666;
  text-align: center;
  padding: $spacing-8 0;
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: $spacing-5;
  padding: $spacing-4;
  border-top: 1px solid #e9ecef;
  background-color: var(--vt-c-white-soft);
  border-radius: 0 0 8px 8px;
}

.pagination-info {
  @include typography-small;
  color: #666;
}

.pagination-controls {
  display: flex;
  gap: $spacing-2-5;
}

.pagination-controls button {
  padding: $spacing-2 $spacing-4;
  border: 1px solid #e0e0e0;
  background-color: var(--vt-c-white);
  border-radius: 8px;
  @include font-weight-medium;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-controls button:hover:not(:disabled) {
  background-color: var(--vt-c-white-soft);
}

.pagination-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-controls .current-page {
  @include font-weight-semibold;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

/* RMA Details Popup Styles */
.rma-details-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background-color: white;
  border-radius: $radius-lg;
  box-shadow: 0 10px 30px rgba(black, 0.2);
  z-index: $z-modal;
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translate(-50%, -48%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
}

.rma-details-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.rma-details-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: $spacing-6 $spacing-8;
  border-bottom: 1px solid rgba(black, 0.1);
  background-color: var(--vt-c-white);
  color: #333;
  border-radius: $radius-lg $radius-lg 0 0;
}

.rma-header-content {
  display: flex;
  flex-direction: column;
  gap: $spacing-3;
}

.rma-details-header h2 {
  @include typography-h3;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.rma-number-status {
  display: flex;
  align-items: center;
  gap: $gap-md;
  flex-wrap: wrap;
}

.rma-number {
  @include typography-body;
  color: #555;
}

.rma-container {
  padding: $card-padding;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
}

/* New Header Styles */
.page-header {
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
}

.main-title {
  @include typography-h2;
  @include font-weight-semibold;
  color: #333;
  margin: 0;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: $spacing-5;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.subtitle {
  @include typography-body-large;
  @include font-weight-medium;
  color: #555;
  margin: 0;
}

.rma-container .description {
  @include typography-small;
  color: #757575;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
}

.new-return-btn {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  border-radius: 8px;
  @include typography-small;
  @include font-weight-medium;
  padding: $button-padding-y $spacing-6;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(var(--vt-c-teal-dark), 0.2);
}

.new-return-btn:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
  box-shadow: 0 4px 8px rgba(var(--vt-c-teal-dark), 0.3);
  transform: translateY(-1px);
}

/* Search and Filters Row */
.search-filters-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: $spacing-3;
  margin-bottom: 24px;
}

.search-wrapper {
  flex: 0 0 350px;
}

.search-input-field {
  width: 100%;
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  @include typography-small;
  padding: $spacing-2 $spacing-4;
  transition: border-color 0.2s;
}

.search-input-field:focus {
  outline: none;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1);
}

.filters-btn {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  height: 40px;
  padding: $spacing-2 $spacing-5;
  background-color: white;
  border: 1px solid #e0e0e0;
  @include typography-small;
  @include font-weight-medium;
  border-radius: 8px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filters-btn:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

/* Filters Side Panel */
.filters-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(black, 0.5);
  z-index: $z-modal-backdrop;
}

.filters-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background-color: white;
  box-shadow: -2px 0 12px rgba(black, 0.2);
  z-index: $z-modal;
  display: flex;
  flex-direction: column;
}

.filters-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-5 $spacing-6;
  border-bottom: 1px solid #e0e0e0;
}

.filters-panel-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  color: #333;
  margin: 0;
}

.close-btn {
  @include typography-h2;
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: var(--vt-c-white-soft);
}

.filters-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: $spacing-6;
  display: flex;
  flex-direction: column;
  gap: $gap-lg;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.filter-label {
  @include typography-small;
  @include font-weight-medium;
  color: #555;
}

.date-range-inputs {
  display: flex;
  align-items: center;
  gap: $gap-sm;
}

.date-input {
  flex: 1;
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  @include typography-small;
  padding: $spacing-2 $spacing-3;
  transition: border-color 0.2s;
}

.date-input:focus {
  outline: none;
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1);
}

.date-separator {
  @include font-weight-medium;
  color: #999999;
}

.filter-dropdown {
  width: 100%;
  height: 40px;
}

/* Simple Checkbox Styles */
.simple-checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: $spacing-2-5;
  padding: $spacing-2 0;
}

.simple-checkbox {
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  cursor: pointer;
  accent-color: var(--vt-c-teal-dark);
  margin: 0;
  flex-shrink: 0;
  appearance: checkbox;
  -webkit-appearance: checkbox;
  -moz-appearance: checkbox;
}

.simple-checkbox-label {
  @include typography-small;
  color: #555;
  cursor: pointer;
  user-select: none;
  margin: 0;
  line-height: 1.4;
}

.filters-panel-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: $spacing-2-5;
  margin-top: auto;
  padding-top: $spacing-6;
  border-top: 1px solid #e0e0e0;
}

.btn-apply-small {
  height: 36px;
  padding: 0 20px;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-small;
  @include font-weight-medium;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-apply-small:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
  box-shadow: 0 2px 6px rgba(var(--vt-c-teal-dark), 0.3);
}

.btn-clear-small {
  height: 36px;
  padding: 0 20px;
  background-color: white;
  color: #555;
  border: 1px solid #e0e0e0;
  @include typography-small;
  @include font-weight-medium;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-clear-small:hover {
  background-color: var(--vt-c-white-soft);
  border-color: #999999;
}

/* Slide Animations */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    width: 100%;
  }

  .new-return-btn {
    width: 100%;
  }

  .search-filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrapper {
    flex: 1;
  }

  .filters-panel {
    width: 100%;
    max-width: 400px;
  }
}

/* Kendo Dropdown Overrides for Filter Panel */
.filters-panel .k-dropdown,
.filters-panel .k-dropdown-wrap {
  width: 100% !important;
  height: 40px !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: $radius-md !important;
  background-color: white !important;
}

.filters-panel .k-dropdown .k-input,
.filters-panel .k-dropdown-wrap .k-input {
  @include typography-small;
  height: 38px !important;
  padding: $spacing-2 $spacing-3 !important;
  line-height: 1.5 !important;
}

.filters-panel .k-dropdown:hover,
.filters-panel .k-dropdown-wrap:hover {
  /* !important required: Override Kendo dropdown border which uses its own defaults */
  border-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark)) !important;
}

.filters-panel .k-dropdown .k-select {
  border-left: 1px solid #e0e0e0 !important;
  width: 32px !important;
}

.rma-table-container {
  overflow-x: auto;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(black, 0.1);
}


.status-badge, .return-status-badge, .rma-status-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  padding: $spacing-1 $spacing-2-5;
  border-radius: $radius-2xl;
  color: white;
  text-align: center;
  min-width: 100px;
}

.return-status-badge {
  @include typography-body;
  padding: $spacing-2 $spacing-4;
  margin-bottom: $spacing-4;
  min-width: 120px;
}

.rma-status-badge {
  background-color: rgba(white, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table {
  border-collapse: separate;
  border-spacing: 0;
  box-shadow: 0 1px 6px black;
}

.table th, .table td {
  border-top: none;
  border-bottom: 1px solid black;
  padding: 0.75rem 1rem;
}

.table th {
  @include font-weight-medium;
  color: #5c6b7a;
  letter-spacing: 0.3px;
  border-bottom: 1px solid rgba(black, 0.1);
  background-color: rgba(#fefefe, 0.7);
}

.table-hover tbody tr:hover {
  background-color: var(--vt-c-white-soft);
}

.table-light {
  background-color: var(--vt-c-white-soft);
}

/* Eliminar bordes en la última fila */
.table tbody tr:last-child td {
  border-bottom: none;
}

.rma-link {
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  text-decoration: underline;
  cursor: pointer;
}

.no-results {
  @include typography-body;
  color: #666;
  text-align: center;
  padding: $spacing-8 0;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: $spacing-4;
  border-top: 1px solid #e9ecef;
  background-color: var(--vt-c-white-soft);
  border-radius: 0 0 8px 8px;
}

.pagination-info {
  @include typography-small;
  color: #666;
}

.pagination-controls {
  display: flex;
  gap: $spacing-2-5;
}

.pagination-controls button {
  padding: $spacing-2 $spacing-4;
  border: 1px solid #e0e0e0;
  background-color: var(--vt-c-white);
  border-radius: 8px;
  @include font-weight-medium;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-controls button:hover:not(:disabled) {
  background-color: var(--vt-c-white-soft);
}

.pagination-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-controls .current-page {
  @include font-weight-semibold;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.rma-details-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  background-color: white;
  border-radius: $radius-lg;
  box-shadow: 0 10px 30px rgba(black, 0.2);
  z-index: $z-modal;
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translate(-50%, -48%); }
  to { opacity: 1; transform: translate(-50%, -50%); }
}

.rma-details-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.rma-details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-5 $spacing-6;
  border-bottom: 1px solid #e9ecef;
  background: white;
  color: #333;
  border-radius: 8px 8px 0 0;
}

.rma-header-content {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.rma-details-header h2 {
  @include typography-h3;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.rma-status-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  padding: 4xx 2x;
  border-radius: $radius-2xl;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-close {
  @include typography-h4;
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.btn-close:hover {
  background-color: rgba(black, 0.1);
}

.close-icon {
  line-height: 1;
}

.rma-details-content {
  padding: $spacing-6;
  flex-grow: 1;
}

.rma-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $spacing-5;
  margin-bottom: 24px;
}

.rma-details-section {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
}

.rma-details-section.full-width {
  grid-column: 1 / -1;
}

.section-title {
  @include typography-body;
  @include font-weight-semibold;
  margin-bottom: 14px;
  color: #444;
  border-bottom: 1px solid black;
  padding-bottom: 8px;
  letter-spacing: 0.3px;
}

.section-icon {
  @include typography-h4;
  margin-right: 10px;
}

.rma-details-card {
  background-color: var(--vt-c-white);
  border-radius: 8px;
  padding: $spacing-4;
  box-shadow: 0 1px 3px rgba(black, 0.1);
  border: 1px solid black;
  height: 100%;
}

.rma-details-row {
  display: flex;
  margin-bottom: 12px;
  align-items: baseline;
  flex-wrap: wrap;
}

.rma-details-row:last-child {
  margin-bottom: 0;
}

.rma-details-label {
  @include typography-small;
  @include font-weight-medium;
  width: 40%;
  color: #666;
  padding-right: 10px;
}

.rma-details-value {
  @include typography-body;
  width: 60%;
  color: #333;
}

.status-value {
  @include font-weight-semibold;
}

.text-open { color: #4caf50; }
.text-closed { color: #999999; }
.text-cancelled { color: #f44336; }
.text-onhold { color: #ff9800; }
.text-new { color: #2196f3; }
.text-awaitingreturn { color: #9c27b0; }
.text-willbeshipped { color: #3f51b5; }
.text-missingphoto { color: #ff5722; }
.text-paymentprocessingerror { color: #e91e63; }

.financial-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $gap-md;
}

.financial-item {
  text-align: center;
  padding: $spacing-4;
  background-color: var(--vt-c-white);
  border-radius: 8px;
  border: 1px solid black;
  box-shadow: 0 1px 2px black;
}

.financial-item.total {
  @include font-weight-semibold;
  background-color: var(--vt-c-white);
  border: 1px solid black;
}

.financial-label {
  @include typography-small;
  @include font-weight-medium;
  color: #666;
  margin-bottom: 8px;
}

.financial-value {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
}

.financial-item.total .financial-value {
  color: #2e7d32;
}

.mt-4 {
  margin-top: 1.5rem;
}

.payment-options h4 {
  @include typography-body-large;
  @include font-weight-semibold;
  margin-bottom: 1rem;
  color: #444;
}

.payment-options-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $spacing-4;
}

.payment-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-2-5 $spacing-4;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(black, 0.1);
}

.option-label {
  @include font-weight-medium;
  color: #555;
}

.option-badge {
  @include typography-small;
  @include font-weight-semibold;
  display: inline-block;
  padding: $spacing-1 $spacing-2-5;
  border-radius: $radius-lg;
}

.option-badge.enabled {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.option-badge.disabled {
  background-color: var(--vt-c-white-soft);
  color: #757575;
}

.processing-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $spacing-4;
}

.processing-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-3 $spacing-4;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(black, 0.1);
}

.processing-label {
  @include font-weight-medium;
  color: #555;
}

.status-indicator {
  @include typography-small;
  @include font-weight-medium;
  display: inline-block;
  padding: $spacing-1 $spacing-2-5;
  border-radius: $radius-lg;
}

.status-success {
  background-color: rgba(#198754, 0.1);
  color: #155724;
}

.status-pending {
  background-color: #fff8e1;
  color: #f57c00;
}

.status-warning {
  background-color: #ffebee;
  color: #c92a2a;
}

.mt-3 {
  margin-top: 1rem;
}

.badge {
  @include typography-caption;
  @include font-weight-medium;
  padding: 0.4em 0.7em;
  border-radius: $radius-lg;
  letter-spacing: 0.3px;
  box-shadow: none;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.rma-details-header .badge {
  @include typography-caption;
  padding: 0.5em 0.8em;
  margin-left: 10px;
}

.return-status-badge {
  @include typography-small;
  @include font-weight-medium;
  display: inline-block;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-xl;
  text-align: center;
  margin-bottom: 16px;
  background-color: var(--vt-c-white-soft);
  color: #444;
  letter-spacing: 0.3px;
  border: 1px solid rgba(black, 0.1);
}

.return-details {
  border-top: 1px solid #e9ecef;
  padding-top: $spacing-4;
}

.rma-note {
  padding: $spacing-4;
  background-color: var(--vt-c-white);
  border: 1px solid rgba(black, 0.1);
  height: 100%;
}

.rma-note p {
  @include typography-body;
  margin: 0;
  white-space: pre-wrap;
  color: #555;
  line-height: 1.6;
}

/* Semantic classes for inline style migration */
.product-name {
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 4px;
}

.financial-total {
  @include font-weight-semibold;
}

.rma-details-footer {
  padding: $spacing-4 $spacing-6;
  border-top: 1px solid black;
  text-align: right;
  background-color: var(--vt-c-white);
  border-radius: 0 0 8px 8px;
}

.btn-primary {
  @include font-weight-medium;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  padding: $spacing-2-5 $spacing-5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(black, 0.1);
}

.btn-primary:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
  box-shadow: 0 2px 5px rgba(black, 0.2);
  transform: translateY(-1px);
}

/* Mobile Card View */
.rma-desktop-grid-view {
  display: block;
}

.rma-mobile-card-view {
  display: none;
  margin-top: $spacing-4;
}

.rma-card {
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
  margin-bottom: $spacing-4;
  transition: box-shadow 0.2s ease;
}

.rma-card:hover {
  box-shadow: 0 4px 12px rgba(black, 0.1);
}

.rma-card-header {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing-2-5;
}

.rma-card-title {
  @include typography-small;
  @include font-weight-semibold;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  text-decoration: underline;
}

.rma-card-body {
  padding: $spacing-4;
}

.rma-card-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: $spacing-2 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
  gap: $spacing-2-5;
}

.rma-card-row:last-child {
  border-bottom: none;
}

.rma-card-label {
  @include typography-small;
  @include font-weight-medium;
  color: #666;
  flex-shrink: 0;
}

.rma-card-value {
  @include typography-small;
  color: #333;
  text-align: right;
  word-break: break-word;
}

@media (max-width: 768px) {
  .rma-desktop-grid-view {
    display: none;
  }

  .rma-mobile-card-view {
    display: block;
  }
}

</style>
